// ════════════════════════════════════════════════════════════════════════════
//  ManDana — record links
//  ---------------------------------------------------------------------------
//  The writing stays human: [[A title]]. Identity is carried beside the source,
//  because titles can change and two records can have the same one. A binding
//  names a token by its source offset and names its target by stable type + id.
//
//  Backlinks are deliberately derived, not stored. Each source document is the
//  sole owner of its outgoing links, so sync, undo and deletion cannot leave a
//  second graph document disagreeing with the writing that created it.
// ════════════════════════════════════════════════════════════════════════════

interface MdWikiTarget { type: string; id: string }
interface MdWikiBinding { start: number; label: string; target: MdWikiTarget }
interface MdWikiToken { start: number; end: number; innerStart: number; innerEnd: number; label: string }

(function (g: any) {
  'use strict';

  const MAX_LABEL = 120;
  const TOKEN_RE = /\[\[([^\[\]\n]{1,120})\]\]/g;

  function mdWikiTarget(value: unknown): MdWikiTarget | null {
    if (!value || typeof value !== 'object') return null;
    const v = value as { type?: unknown; id?: unknown };
    const ref = g.mdRecordRef ? g.mdRecordRef(v.type, v.id) : null;
    return ref ? { type: ref.type, id: ref.id } : null;
  }

  function mdWikiTargetKey(value: unknown): string {
    const target = mdWikiTarget(value);
    return target ? `${target.type}:${target.id}` : '';
  }

  function mdWikiTokens(text: unknown): MdWikiToken[] {
    if (typeof text !== 'string' || !text) return [];
    const out: MdWikiToken[] = [];
    TOKEN_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = TOKEN_RE.exec(text))) {
      const label = (match[1] || '').trim();
      if (!label) continue;
      out.push({ start: match.index, end: match.index + match[0].length,
        innerStart: match.index + 2, innerEnd: match.index + match[0].length - 2,
        label: label.slice(0, MAX_LABEL) });
    }
    return out;
  }

  function cleanBindings(value: unknown): MdWikiBinding[] {
    if (!Array.isArray(value)) return [];
    const out: MdWikiBinding[] = [];
    const seen = new Set<number>();
    for (const row of value) {
      if (!row || typeof row !== 'object') continue;
      const r = row as { start?: unknown; label?: unknown; target?: unknown };
      if (!Number.isInteger(r.start) || (r.start as number) < 0 || seen.has(r.start as number)) continue;
      const label = typeof r.label === 'string' ? r.label.trim().slice(0, MAX_LABEL) : '';
      const target = mdWikiTarget(r.target);
      if (!label || !target) continue;
      seen.add(r.start as number);
      out.push({ start: r.start as number, label, target });
    }
    return out.sort((a, b) => a.start - b.start);
  }

  /** Keep only bindings that still point at the same token in this source. */
  function mdWikiBindings(value: unknown, text?: unknown): MdWikiBinding[] {
    const clean = cleanBindings(value);
    if (typeof text !== 'string') return clean;
    const tokens = new Map(mdWikiTokens(text).map((token) => [token.start, token]));
    return clean.filter((row) => {
      const token = tokens.get(row.start);
      return !!token && token.label === row.label;
    });
  }

  function commonPrefix(a: string, b: string): number {
    const n = Math.min(a.length, b.length);
    let i = 0;
    while (i < n && a.charCodeAt(i) === b.charCodeAt(i)) i++;
    return i;
  }

  function commonSuffix(a: string, b: string, prefix: number): number {
    const max = Math.min(a.length, b.length) - prefix;
    let i = 0;
    while (i < max && a.charCodeAt(a.length - 1 - i) === b.charCodeAt(b.length - 1 - i)) i++;
    return i;
  }

  /** Reconcile identity after an arbitrary source edit. Tokens wholly outside
      the edited span retain their targets; a token touched by the edit becomes
      unresolved until the picker explicitly binds it again. */
  function mdWikiReconcile(previous: unknown, next: unknown, value: unknown): MdWikiBinding[] {
    const before = typeof previous === 'string' ? previous : '';
    const after = typeof next === 'string' ? next : '';
    const bindings = mdWikiBindings(value, before);
    if (!bindings.length || before === after) return mdWikiBindings(bindings, after);
    const prefix = commonPrefix(before, after);
    const suffix = commonSuffix(before, after, prefix);
    const oldTail = before.length - suffix;
    const delta = after.length - before.length;
    const moved = bindings.flatMap((row) => {
      const token = mdWikiTokens(before).find((t) => t.start === row.start);
      if (!token) return [];
      if (token.end <= prefix) return [row];
      if (token.start >= oldTail) return [{ ...row, start: row.start + delta }];
      return [];
    });
    return mdWikiBindings(moved, after);
  }

  /** Bind one complete token, replacing any prior identity at that offset. */
  function mdWikiBind(text: unknown, value: unknown, start: unknown, targetValue: unknown): MdWikiBinding[] {
    if (typeof text !== 'string' || !Number.isInteger(start)) return mdWikiBindings(value, text);
    const token = mdWikiTokens(text).find((t) => t.start === start);
    const target = mdWikiTarget(targetValue);
    if (!token || !target) return mdWikiBindings(value, text);
    const rest = mdWikiBindings(value, text).filter((row) => row.start !== start);
    return [...rest, { start: start as number, label: token.label, target }]
      .sort((a, b) => a.start - b.start);
  }

  /** Tokens in reading order, each paired with its identity when resolved. */
  function mdWikiLinks(text: unknown, value: unknown): Array<MdWikiToken & { target: MdWikiTarget | null }> {
    const byStart = new Map(mdWikiBindings(value, text).map((row) => [row.start, row.target]));
    return mdWikiTokens(text).map((token) => ({ ...token, target: byStart.get(token.start) || null }));
  }

  /** Unwrap [[Title]] to Title. This is the reversible, non-destructive unlink. */
  function mdWikiUnlink(text: unknown, value: unknown, start: unknown): { text: string; links: MdWikiBinding[]; caret: number } | null {
    if (typeof text !== 'string' || !Number.isInteger(start)) return null;
    const token = mdWikiTokens(text).find((t) => t.start === start);
    if (!token) return null;
    const next = text.slice(0, token.start) + token.label + text.slice(token.end);
    return { text: next, links: mdWikiReconcile(text, next, value), caret: token.start + token.label.length };
  }

  g.mdWikiTarget = mdWikiTarget;
  g.mdWikiTargetKey = mdWikiTargetKey;
  g.mdWikiTokens = mdWikiTokens;
  g.mdWikiBindings = mdWikiBindings;
  g.mdWikiReconcile = mdWikiReconcile;
  g.mdWikiBind = mdWikiBind;
  g.mdWikiLinks = mdWikiLinks;
  g.mdWikiUnlink = mdWikiUnlink;

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { mdWikiTarget, mdWikiTargetKey, mdWikiTokens, mdWikiBindings,
      mdWikiReconcile, mdWikiBind, mdWikiLinks, mdWikiUnlink };
  }
})(typeof window !== 'undefined' ? window : globalThis);
